//Built-in libraries

import { yupResolver } from '@hookform/resolvers/yup';
import { useQueries } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
//Third party libraries
import * as Yup from 'yup';
import { getRolePermission } from '@/api/roles-and-permission';
import type { ReactSelectOption } from '@/types/Constants';
//Assets andd Miscellaneous
import { States } from '../../../../../constants';
import { constructionType, projectType } from '../../../../../constants/Project';
import { useUsers } from '../../../../../hooks/ReactQueryHooks/useUsersQuery';
//Services
import { projectsSelector } from '../../../../../store';
import type { User } from '../../../../../types/User';
import { compareTwoArrays, containsObjectInArray } from '../../../../../utils';
import { zipCodeRegEx } from '../../../../../utils/RegularExpressions';
//Custom components
import { HookFormProvider } from '../../../../UI/HookForm/Provider';
import { BasicProjectInfoForm } from './BasicProjectInfoForm';
import { projectLeadRequiredPermissions, projectTypeWithAvgSalesPrice, projectTypeWithUnits } from './constants';

//Validation Schema needs
const validationSchema = Yup.object({
  name: Yup.string().required('Required').max(50, 'Project name cannot be greater than 50 characters.'),
  address: Yup.string()
    .required('Required')
    .min(4, 'At least 4 characters address is required')
    .max(50, 'Address cannot be greater than 50 characters.'),
  city: Yup.string()
    .required('Required')
    .min(2, 'At least 2 characters city name is required')
    .max(50, 'City name cannot be greater than 50 characters.'),
  state: Yup.object({
    name: Yup.string(),
    abbreviation: Yup.string(),
  })
    .typeError('Required')
    .required('Required')
    .test('state', 'You must select a valid state', (val) => {
      return containsObjectInArray(States as { name: string; abbreviation: string }[], val);
    }),
  zipCode: Yup.string()
    .required('Required')
    .matches(zipCodeRegEx, 'Zip / Postal Code must be a 5 digit number')
    .max(5, 'Zip / Postal Code must be 5 digits')
    .min(5, 'Zip / Postal Code must be 5 digits'),
  projectType: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  })
    .typeError('Required')
    .required('Required')
    .test('projectType', 'You must select a valid project type', (val) => {
      return containsObjectInArray(projectType, val);
    }),
  units: Yup.number().when('projectType', {
    is: (val: ReactSelectOption) => projectTypeWithUnits.includes(val.value),
    then: (schema) => schema.required('Required').positive().max(999999999999999),
    otherwise: (schema) => schema.nullable(),
  }),
  avgSalesPrice: Yup.number().when('projectType', {
    is: (val: ReactSelectOption) => projectTypeWithAvgSalesPrice.includes(val.value),
    then: (schema) => schema.required('Required').positive().max(999999999999999),
    otherwise: (schema) => schema.nullable(),
  }),
  constructionType: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  })
    .typeError('Required')
    .required('Required')
    .test('constructionType', 'You must select a valid construction type', (val) => {
      return containsObjectInArray(constructionType, val);
    }),
  neighborhood: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  }).nullable(),
  squareFeet: Yup.number().typeError('Square Feet must be a number.').positive('Square Feet cannot be in negative').required('Required'),
  goals: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().required('Required'),
        percentage: Yup.number()
          .typeError('Percentage must be a number.')
          .required('Percentage cannot be empty')
          .min(0, 'Percentage must be greater than 0')
          .max(100, 'Percentage cannot be greater than 100%'),
      })
    )
    .test('is-small-than-100', 'Total social goal percentage cannot be greater than 100%', (goals) => {
      const totalPercentage = goals?.reduce((total, goal) => {
        total += Number(goal.percentage || 0);
        return total;
      }, 0);

      return !!(typeof totalPercentage === 'number' && totalPercentage <= 100);
    }),
});

//Component Begin
const BasicProjectInfo = ({ handleNext }: { handleNext: (type: string) => void }) => {
  const { Project } = useSelector(projectsSelector);
  const projectBaselineInfoState = Project.BaselineProjectInfo;

  const fetchUsers = useUsers({
    params: { active: 'true' },
  });
  const users = fetchUsers.data?.data;

  //Only users who have all project permission can become a project lead
  const authorizedUsers = useQueries({
    queries: users
      ? users?.map((user: User) => {
          return {
            queryKey: ['user', user.id],
            queryFn: () =>
              getRolePermission(String(user.roleId)).then((permissions: any) => {
                const projectPermissions = permissions.Project.filter((pp: any) => pp.allow)
                  .map((p: any) => p.slug)
                  .filter((ps: string) => ps !== 'LIST_ALL_PROJECTS');

                const { array2Diff } = compareTwoArrays(projectPermissions, projectLeadRequiredPermissions);

                return {
                  label: `${user.name} <${user.email}>`,
                  value: user.id.toString(),
                  authorized: array2Diff.length === 0,
                };
              }),
          };
        })
      : [],
  });

  //Only Project Manager role and above can
  //become project lead
  // const authorizedProjectLeadUsers: User[] = [];
  // const authorizedProjectLeadRoles = [
  //   "SUPER ADMIN",
  //   "ADMIN",
  //   "PROJECT MANAGER",
  // ];

  // users?.forEach((user: User) => {
  //   // if (user.roleId) {
  //   //   getRolePermission(user.roleId).then((permissions: any) => {
  //   //     const projectPermissions = permissions.Project.map(
  //   //       (pp: any) => pp.slug
  //   //     ).filter((p: string) => p !== "LIST_ALL_PROJECTS");
  //   //     const { array2Diff } = compareTwoArrays(
  //   //       projectPermissions,
  //   //       projectLeadRequiredPermissions
  //   //     );
  //   //     if (array2Diff.length === 0) {
  //   //       authorizedProjectLeadUsers.push(user);
  //   //     }
  //   //   });
  //   // }
  //   if (authorizedProjectLeadRoles.includes(user.role.toUpperCase())) {
  //     authorizedProjectLeadUsers.push(user);
  //   }
  // });

  // const validProjectLeadOptions = authorizedProjectLeadUsers.map(
  //   ({ id, name, email }) => ({
  //     label: `${name} <${email}>`,
  //     value: id.toString(),
  //   })
  // );

  const validProjectLeadOptions = authorizedUsers
    .filter((au: any) => au.data?.authorized)
    .map((u: any) => ({
      label: u.data?.label,
      value: u.data?.value,
    }));

  const projectLeadSchema = Yup.object({
    projectLeadId: Yup.object({
      label: Yup.string(),
      value: Yup.string(),
    })
      .typeError('Project Lead is required')
      .required('Project Lead is required')
      .test('projectLead', 'You must select a valid user', (val) => {
        return containsObjectInArray(validProjectLeadOptions, val);
      }),
  });

  return (
    <HookFormProvider
      resolver={yupResolver(validationSchema.concat(projectLeadSchema)) as any}
      defaultValues={projectBaselineInfoState}
      mode='all'
    >
      <BasicProjectInfoForm handleNext={handleNext} authorizedProjectLeadUsers={validProjectLeadOptions} />
    </HookFormProvider>
  );
};

export default BasicProjectInfo;
